import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { ArrowLeft, Award, Users, Clock, Truck } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

function About({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const meta = pickMeta('about', currentLang);

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo lang={currentLang} path="about" title={meta.title} description={meta.description} />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={`/${currentLang}`} className="flex items-center">
              <img 
                src="/logo.jpg" 
                alt="Ainanan" 
                className="h-10 object-contain"
              />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to={`/${currentLang}`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.catalog')}</Link>
              <Link to={`/${currentLang}/about`} className="text-sm font-medium text-gray-900">{t('common.about')}</Link>
              <Link to={`/${currentLang}/contacts`} className="text-sm font-medium text-gray-600 hover:text-stone-700">{t('common.contacts')}</Link>
            </nav>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
              {user ? (
                <Button variant="ghost" size="sm" onClick={onLogout}>{t('common.logout')}</Button>
              ) : (
                <Link to={`/${currentLang}/login`}>
                  <Button variant="ghost" size="sm">{t('common.login')}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-700 hover:text-stone-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('about.backToCatalog')}
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-6">{t('about.heading')}</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-600 mb-8">
            {t('about.intro')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
            <div className="bg-white p-6 rounded-xl border">
              <Award className="w-10 h-10 text-stone-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('about.quality')}</h3>
              <p className="text-gray-600">{t('about.qualityDesc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border">
              <Clock className="w-10 h-10 text-stone-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('about.freshness')}</h3>
              <p className="text-gray-600">{t('about.freshnessDesc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border">
              <Users className="w-10 h-10 text-stone-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('about.b2bPartnership')}</h3>
              <p className="text-gray-600">{t('about.b2bDesc')}</p>
            </div>
            <div className="bg-white p-6 rounded-xl border">
              <Truck className="w-10 h-10 text-stone-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('about.delivery')}</h3>
              <p className="text-gray-600">{t('about.deliveryDesc')}</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">{t('about.history')}</h2>
          <p className="text-gray-600 mb-4">
            {t('about.historyText1')}
          </p>
          <p className="text-gray-600 mb-4">
            {t('about.historyText2')}
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">{t('about.forBusiness')}</h2>
          <p className="text-gray-600 mb-4">
            {t('about.businessOffer')}
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-8">
            <li>{t('about.businessList1')}</li>
            <li>{t('about.businessList2')}</li>
            <li>{t('about.businessList3')}</li>
            <li>{t('about.businessList4')}</li>
          </ul>

          <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 mt-8">
            <h3 className="text-lg font-semibold text-stone-800 mb-2">{t('about.becomePartner')}</h3>
            <p className="text-stone-700 mb-4">{t('about.partnerCta')}</p>
            <Link to={`/${currentLang}/contacts`}>
              <Button className="bg-emerald-700 hover:bg-emerald-800">{t('about.contactUs')}</Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default About;
